import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { FiltersValue } from '../components/Filters';
import { SearchAutocompleteItem } from '../components/SearchAutocomplete';
import {
	buildAvailabilitySearch,
	parseFiltersFromSearch,
	parsePageFromSearch,
	parsePerPageFromSearch,
	parseSearchTokens,
} from '../utils/availabilityQueryParams';
import { buildGroupSlug } from '../utils/availabilitySlug';
import { useAvailabilitySlugItems } from './useAvailabilitySlugItems';

function sameStringArray(a: string[], b: string[]): boolean {
	return a.join(',') === b.join(',');
}

export interface AvailabilityLabelSource {
	id: string;
	atc: string;
	rs: string | null;
}

function rememberItems(cache: Map<string, SearchAutocompleteItem>, items: SearchAutocompleteItem[]) {
	for (const item of items) {
		cache.set(item.id, item);
		if (item.slug) {
			cache.set(item.slug, item);
		}
	}
}

function itemsFromUrl(
	search: string,
	itemsBySlug: Record<string, SearchAutocompleteItem>,
	cache: Map<string, SearchAutocompleteItem>
): SearchAutocompleteItem[] {
	return parseSearchTokens(search).flatMap((token) => {
		if (token.kind === 'uuid') {
			return [cache.get(token.value) ?? { id: token.value, label: token.value }];
		}

		const resolved = itemsBySlug[token.value] ?? cache.get(token.value);
		return resolved ? [resolved] : [];
	});
}

// Owns the Availability filter/pagination state and keeps it in sync with the URL.
// - Writes state changes back to the URL (shareable, refresh-safe).
// - Reads the URL on external navigation (browser back/forward), skipping updates that
//   already match the current state so resolved chip labels are not clobbered.
export function useAvailabilityFilters() {
	const location = useLocation();
	const history = useHistory();
	const itemCache = useRef(new Map<string, SearchAutocompleteItem>());

	const searchTokens = useMemo(() => parseSearchTokens(location.search), [location.search]);
	const slugTokens = useMemo(
		() => searchTokens.filter((token) => token.kind === 'slug').map((token) => token.value),
		[searchTokens]
	);
	const { itemsBySlug, isResolving } = useAvailabilitySlugItems(slugTokens);

	useEffect(() => {
		rememberItems(itemCache.current, Object.values(itemsBySlug));
	}, [itemsBySlug]);

	const urlItems = useMemo(
		() => itemsFromUrl(location.search, itemsBySlug, itemCache.current),
		[itemsBySlug, location.search]
	);
	const urlRs = parseFiltersFromSearch(location.search).rs;
	const urlPage = parsePageFromSearch(location.search);
	const urlPerPage = parsePerPageFromSearch(location.search);

	const [filters, setFilters] = useState<FiltersValue>(() => ({ items: urlItems, rs: urlRs }));
	const [page, setPage] = useState(urlPage);
	const [perPage, setPerPage] = useState(urlPerPage);

	const unresolvedSlugs = slugTokens.filter((slug) => !itemsBySlug[slug] && !itemCache.current.has(slug));
	const isResolvingSlugs = unresolvedSlugs.length > 0 && isResolving;
	const hasSearchTokens = searchTokens.length > 0;

	useEffect(() => {
		if (isResolvingSlugs) {
			return;
		}

		const nextFilters = { items: urlItems, rs: urlRs };
		const matchesState =
			urlPage === page &&
			urlPerPage === perPage &&
			sameStringArray(
				nextFilters.items.map((item) => item.id),
				filters.items.map((item) => item.id)
			) &&
			sameStringArray(nextFilters.rs, filters.rs);

		if (matchesState) {
			return;
		}

		setFilters(nextFilters);
		setPage(urlPage);
		setPerPage(urlPerPage);
	}, [filters, isResolvingSlugs, page, perPage, urlItems, urlPage, urlPerPage, urlRs]);

	const pushSearch = (nextFilters: FiltersValue, nextPage: number, nextPerPage: number) => {
		history.push({
			pathname: location.pathname,
			search: buildAvailabilitySearch(location.search, nextFilters, nextPage, nextPerPage),
		});
	};

	const changeFilters = (value: FiltersValue) => {
		rememberItems(itemCache.current, value.items);
		setFilters(value);
		setPage(1);
		pushSearch(value, 1, perPage);
	};

	const changePage = (nextPage: number) => {
		setPage(nextPage);
		pushSearch(filters, nextPage, perPage);
	};

	const changePerPage = (nextPerPage: number) => {
		setPerPage(nextPerPage);
		setPage(1);
		pushSearch(filters, 1, nextPerPage);
	};

	// The URL only stores item ids or slugs. UUID tokens initially use the id as the chip label.
	// Once matching groups are loaded, resolve the ATC label and canonical slug.
	const resolveLabels = (groups: AvailabilityLabelSource[]) => {
		setFilters((prev) => {
			if (prev.items.length === 0) {
				return prev;
			}

			let changed = false;
			const items = prev.items.map((item) => {
				const match = groups.find((group) => group.id === item.id);
				if (!match) {
					return item;
				}

				const slug = buildGroupSlug({ atc: match.atc, rs: match.rs });
				if (item.label === match.atc && item.slug === slug) {
					return item;
				}

				changed = true;
				const resolved = { id: item.id, label: match.atc, slug };
				itemCache.current.set(item.id, resolved);
				itemCache.current.set(resolved.slug, resolved);
				return resolved;
			});

			return changed ? { ...prev, items } : prev;
		});
	};

	useEffect(() => {
		if (isResolvingSlugs || filters.items.length === 0 || filters.items.some((item) => !item.slug)) {
			return;
		}

		const canonicalSearch = buildAvailabilitySearch(location.search, filters, page, perPage);
		if (canonicalSearch !== location.search.replace(/^\?/, '')) {
			history.replace({ pathname: location.pathname, search: canonicalSearch });
		}
	}, [filters, history, isResolvingSlugs, location.pathname, location.search, page, perPage]);

	return {
		filters,
		page,
		perPage,
		changeFilters,
		changePage,
		changePerPage,
		resolveLabels,
		isResolvingSlugs,
		hasSearchTokens,
	};
}
